<template>
  <form-layout :title="formTitle" bottom-bar-stick-bottom @on-dismiss="tryDismiss">
    <vs-form ref="stopConfirmationForm">
      <JobInventoryGroupPicker
        v-if="inventoryGroup"
        :model-value="inventoryGroup"
        :resource-type-activity-id="jobActivityId ?? -1"
        :is-disabled="!canOverwriteInventoryReceipts || isSkipping"
        :is-location-disabled="isOnlyCreatingReceipts"
        :allow-empty-pickup-location="false"
        :show-add-inventory-group-button="false"
        :allow-remove-inventory-group="false"
        :allow-zero-inventory="true"
        @update:modelValue="inventoryGroup = $event"
      />

      <VsDatePickerNext
        v-if="!isSkipping"
        :key="`${jobId}-arrival-time`"
        class="pb-3"
        :value="arrivalTime"
        label="Arrived at"
        :disabled="isOnlyCreatingReceipts || isSkipping"
        :can-select-past="true"
        :rules="[validateArrivalTime]"
        required
        @input="updateArrivalTime"
      />
      <vs-text-input v-if="isSkipping" v-model="skipComment" required label="Reason for skipping" />
    </vs-form>

    <vs-text>{{ declarationText }} </vs-text>

    <template #snack-bar-wrapper>
      <vs-snackbar
        :value="showSnackbarError"
        sticky
        type="error"
        @input="showSnackbarError = false"
      >
        Failed to submit confirmation
      </vs-snackbar>
    </template>

    <template #action-bar>
      <vs-button
        data-test="receipt-save-button"
        :label="saveButtonLabel"
        :loading="saving"
        :disabled="!hasChanges"
        full-width
        :destructive="isSkipping"
        @click="onSave"
      />
    </template>
  </form-layout>
</template>

<script setup lang="ts">
import { computed, watch, ref, toRef } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { addMinutes, isAfter, isEqual as isSameDate, isValid as isValidDate } from 'date-fns'
import { isEqual } from 'lodash'
import { v4 } from 'uuid'

// Types
import { type InventoryItem, InventoryReceiptType, JobStopType } from '@ironsight/types'
import { InventoryPickerGroup, InventoryPickerItem } from './InventoryPickers'

// Components
import FormLayout from '@/components/common/FormLayout.vue'
import VsForm from '@/components/vision/VsForm.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsDatePickerNext from '@/components/vision/next/inputs/VsDatePickerNext.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import JobInventoryGroupPicker from './InventoryPickers/JobInventoryGroupPicker.vue'

// Composables
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import { useSuggestedInventory } from './useInventoryReceiptSuggestedInventory'
import { useJobStopsAndReceiptsPermissions } from './useJobStopsAndReceiptsPermissions'
import { useUserName } from '@/common/useUserName'
import { useCurrentOrg } from '@/common/useCurrentOrg'
import { useLocationQuery } from '@/api/locations'
import {
  createJobStop,
  updateJobStop,
  updateStopReceipts,
  useSkipRequestedStopMutation,
} from '@/api/jobs'
import { JOBS_FILTERED_QUERY_KEY } from '@/components/jobs/job-search-page/useFilterJobsQuery'
import { standardizeInventoryList } from '@/utils/inventories'
import { useStore } from 'vuex-composition-helpers'

// Props
const props = defineProps<{
  jobId: string
  jobNumber: number
  jobActivityId: number | undefined
}>()

const store = useStore()

// Bottom-sheet State
const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()
const requestedStop = computed(
  () =>
    jobBottomSheetStatesStore.jobBottomSheetStates.stopConfirmationBottomSheetStates.requestedStop,
)
const actualStop = computed(
  () => jobBottomSheetStatesStore.jobBottomSheetStates.stopConfirmationBottomSheetStates.actualStop,
)
const isSkipping = computed(
  () => jobBottomSheetStatesStore.jobBottomSheetStates.stopConfirmationBottomSheetStates.isSkipping,
)
const receiptType = computed(
  () =>
    jobBottomSheetStatesStore.jobBottomSheetStates.stopConfirmationBottomSheetStates.receiptType,
)

// Refs
const stopConfirmationForm = ref<typeof VsForm>()
const inventoryGroup = ref<InventoryPickerGroup>()
const saving = ref(false)
const showSnackbarError = ref(false)
const jobNumberRef = toRef(props, 'jobNumber')
const jobIdRef = toRef(props, 'jobId')
const arrivalTime = ref<Date | null>(new Date())
const skipComment = ref('')

// Composables
const queryClient = useQueryClient()
const { mutateAsync: skipRequestedStop } = useSkipRequestedStopMutation()

const inventoryGroupLocationId = computed(() => inventoryGroup.value?.locationId)
const { data: location } = useLocationQuery(inventoryGroupLocationId)

const locationOwnerId = computed(() => location.value?.ownerId ?? null)
const { canCreateStops, canOverwriteInventoryReceipts } = useJobStopsAndReceiptsPermissions(
  jobNumberRef,
  locationOwnerId,
)

const { currentOrg } = useCurrentOrg()
const { name } = useUserName()

const { suggestedInventory } = useSuggestedInventory(
  jobIdRef,
  requestedStop.value?.id ?? actualStop.value?.requestedStopId ?? null,
  requestedStop.value?.stopType ?? JobStopType.DropOff,
)

// Computed Properties
const isOnlyCreatingReceipts = computed(
  () =>
    !canCreateStops.value ||
    (actualStop.value?.stopType === JobStopType.DropOff &&
      receiptType.value === InventoryReceiptType.Receiver),
)

const hasChanges = computed(() => {
  const existingInventory =
    actualStop.value?.inventoryReceipts.filter(
      (r) => r.ownerOrgId === currentOrg.value?.id && r.receiptType === receiptType.value,
    ) ?? []

  const currentInventory = inventoryGroup.value?.inventory ?? []

  return (
    actualStop.value?.locationId !== inventoryGroupLocationId.value ||
    !isSameDate(
      new Date(actualStop.value?.arrivedAt.valueOf() ?? Number.NaN),
      arrivalTime.value ?? new Date(Number.NaN),
    ) ||
    !isEqual(
      standardizeInventoryList(existingInventory),
      standardizeInventoryList(currentInventory),
    )
  )
})

const formTitle = computed(() =>
  isSkipping.value
    ? `Skip ${
        requestedStop.value?.stopType === JobStopType.PickUp ? 'pickup' : 'dropoff'
      } confirmation`
    : 'Stop confirmation',
)

const declarationText = computed(() =>
  isSkipping.value
    ? ''
    : `I, ${name.value}, confirm that the information provided above is accurate and complete. I acknowledge that I am responsible for the content I have submitted.`,
)

const saveButtonLabel = computed(() =>
  isSkipping.value
    ? `Skip this ${requestedStop.value?.stopType === JobStopType.PickUp ? 'pickup' : 'dropoff'}`
    : 'Save and submit receipt',
)

// Utility Functions
function stopToInventoryPickerGroup(
  locationId: number,
  locationName: string,
  stopType: JobStopType,
  inventory: InventoryItem[],
): InventoryPickerGroup {
  const formatInventoryItems = inventory.map((inventory) => ({
    ...inventory,
    _uniqueId: v4(),
    id: null,
  }))
  return {
    locationId,
    locationName,
    stopType,
    _uniqueId: v4(),
    id: null,
    inventory: formatInventoryItems,
  }
}

function formatInventoryForPayload(inventoryItems: InventoryPickerItem[]) {
  return inventoryItems
    .filter((inventory) => inventory.inventorySubTypeId !== null && inventory.quantity !== null)
    .map((inventory) => ({
      inventorySubTypeId: inventory.inventorySubTypeId!,
      quantity: inventory.quantity!,
    }))
}

function isValid() {
  // Depending on your IDE settings you may see warning here for calling `isValid`.
  // It is a false positive as the VsForm is not composition API yet
  return stopConfirmationForm.value?.isValid()
}

// Event Handlers
const dismiss = () => {
  jobBottomSheetStatesStore.clearJobBottomSheetStates()
}

function tryDismiss() {
  let shouldDismiss = true
  if (hasChanges.value) {
    shouldDismiss = window.confirm(
      'This stop has not been confirmed, are you sure you want to exit?',
    )
  }

  if (shouldDismiss) {
    dismiss()
  }
}

function updateArrivalTime(newArrivalTime: Date | null) {
  arrivalTime.value = newArrivalTime ? new Date(newArrivalTime) : null
}

function validateArrivalTime(value: Date | null) {
  if (isOnlyCreatingReceipts.value || !value || !isValidDate(value)) {
    return undefined
  }

  return isAfter(value, addMinutes(new Date(), 15))
    ? 'Select a date and time that isn’t in the future'
    : undefined
}

async function onSave() {
  if (!isValid()) return

  saving.value = true

  try {
    let stopId = actualStop.value?.id
    const trimmedSkipComment = skipComment.value.trim() || undefined

    // Handle skipping a requested stop
    if (isSkipping.value) {
      if (requestedStop.value?.id) {
        await skipRequestedStop({
          jobId: props.jobId,
          stopId: requestedStop.value.id,
          occurredAt: new Date(),
          skipComment: trimmedSkipComment,
        })
      } else {
        throw new Error('Skipping a stop requires a requested stop id')
      }
    } else if (!isOnlyCreatingReceipts.value) {
      if (stopId) {
        await updateJobStop(
          props.jobId,
          stopId,
          inventoryGroupLocationId.value!,
          requestedStop.value?.stopType ?? JobStopType.PickUp,
          arrivalTime.value!,
        )
      } else {
        stopId = await createJobStop(
          props.jobId,
          inventoryGroupLocationId.value!,
          requestedStop.value?.stopType ?? JobStopType.PickUp,
          requestedStop.value?.id ?? null,
          arrivalTime.value!,
        )
      }
    } else if (!stopId) {
      console.warn('A stop must be confirmed before you can add receipts')
      showSnackbarError.value = true
      return
    }

    if (canOverwriteInventoryReceipts.value && !isSkipping.value && stopId) {
      await updateStopReceipts(
        props.jobId,
        stopId,
        formatInventoryForPayload(inventoryGroup.value?.inventory ?? []),
        new Date(),
        receiptType.value!,
      )
    }

    await Promise.all([
      queryClient.invalidateQueries(['stops', 'byJobId', props.jobId]),
      ...(stopId ? [queryClient.invalidateQueries(['stops', 'byId', stopId])] : []),
    ])

    // We intentionally don't await these other updates it so they don't block the rest of the save from finishing.
    queryClient.invalidateQueries(['geojson-jobs', jobNumberRef.value])
    queryClient.invalidateQueries([JOBS_FILTERED_QUERY_KEY])
    queryClient.invalidateQueries(['jobs', 'search'])
    queryClient.invalidateQueries(['history', 'byJobNumber', props.jobNumber])

    const multiTripGeoJsonQuery = queryClient
      .getQueryCache()
      .getAll()
      .find(
        (q) =>
          q.queryKey[0] === 'geojson-job-trips' &&
          q.queryKey[1] instanceof Array &&
          q.queryKey[1].some((jobId) => jobId === props.jobId),
      )

    if (multiTripGeoJsonQuery) {
      queryClient.invalidateQueries(multiTripGeoJsonQuery.queryKey)
    }

    store.commit('tickets/CLEAR_TICKETS_ERROR')

    dismiss()
  } catch (e) {
    showSnackbarError.value = true
  } finally {
    saving.value = false
  }
}

// Watchers
// Initialize stop information when either the requested stop or existing stop changes
watch(
  [requestedStop, actualStop],
  ([newRequestedStop, newActualStop]) => {
    // we need to be editing an existing stop, or confirming a requested stop
    if (!(newActualStop || newRequestedStop)) {
      return
    }

    if (newActualStop) {
      arrivalTime.value = new Date(newActualStop.arrivedAt.valueOf())
    }

    const newLocationId = newActualStop?.locationId ?? newRequestedStop!.locationId!
    const newLocationName = newActualStop?.locationName ?? newRequestedStop!.locationName!
    const newStopType = newActualStop?.stopType ?? newRequestedStop!.stopType!

    const myReceipts =
      newActualStop?.inventoryReceipts.filter(
        (r) => r.ownerOrgId === currentOrg.value?.id && r.receiptType === receiptType.value,
      ) ?? []

    inventoryGroup.value = stopToInventoryPickerGroup(
      newLocationId,
      newLocationName,
      newStopType,
      myReceipts.length > 0 ? myReceipts : suggestedInventory.value,
    )
  },
  { immediate: true },
)
</script>
